import { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const CATEGORY_LABELS = {
  bug:     '🐛 Bug',
  feature: '💡 Feature',
  general: '💬 General',
};

const STATUS_COLOURS = {
  new:      'bg-danger',
  read:     'bg-secondary',
  resolved: 'bg-success',
};

/**
 * AdminFeedbackPage — viewable at /admin/feedback.
 * Only accessible when profile.isAdmin === true.
 *
 * To grant yourself access, set isAdmin: true on your users/{uid} document
 * in the Firebase console.
 */
// Auth gate is handled by AdminLayout — this page renders only for admins.
export default function AdminFeedbackPage() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all'); // all | new | read | resolved

  useEffect(() => {
    getDocs(collection(db, 'supportRequests'))
      .then((snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Newest first — client-side sort avoids composite Firestore index
        docs.sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1));
        setItems(docs);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, status: newStatus } : i));
    await updateDoc(doc(db, 'supportRequests', id), { status: newStatus });
  };

  const filtered = filter === 'all' ? items : items.filter((i) => i.status === filter);
  const newCount = items.filter((i) => i.status === 'new').length;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '2rem 1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h1 className="page-header-title">📬 User Feedback</h1>
          <p className="page-header-subtitle">{items.length} total · {newCount} new</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {['all', 'new', 'read', 'resolved'].map((f) => (
          <button
            key={f}
            className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{ borderRadius: 50, textTransform: 'capitalize' }}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? `All (${items.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${items.filter((i) => i.status === f).length})`}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5" style={{ color: 'var(--text-light)' }}>
            No {filter === 'all' ? '' : filter} feedback yet.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map((item) => (
            <FeedbackCard key={item.id} item={item} onStatusChange={handleStatusChange} />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackCard({ item, onStatusChange }) {
  const date = new Date(item.createdAt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="card" style={{ opacity: item.status === 'resolved' ? 0.65 : 1 }}>
      <div className="card-body" style={{ padding: '0.875rem 1rem' }}>
        {/* Top row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.625rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={`badge ${STATUS_COLOURS[item.status] || 'bg-secondary'}`} style={{ fontSize: '0.68rem' }}>
              {item.status || 'new'}
            </span>
            <span className="badge bg-light text-dark border" style={{ fontSize: '0.68rem' }}>
              {CATEGORY_LABELS[item.category] || item.category}
            </span>
            {item.displayName && (
              <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-dark)' }}>
                {item.displayName}
              </span>
            )}
            {item.role && (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>({item.role})</span>
            )}
            <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{item.email}</span>
          </div>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {date}
          </span>
        </div>

        {/* Message */}
        <p style={{ margin: '0 0 0.625rem', fontSize: '0.9rem', color: 'var(--text-dark)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {item.message}
        </p>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          {item.url && (
            <span style={{ fontSize: '0.72rem', color: 'var(--text-light)' }}>
              {item.url}
            </span>
          )}
          <div style={{ display: 'flex', gap: '0.375rem', marginLeft: 'auto' }}>
            {item.status !== 'read' && (
              <button
                className="btn btn-outline-secondary btn-sm"
                style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem' }}
                onClick={() => onStatusChange(item.id, 'read')}
              >
                Mark read
              </button>
            )}
            {item.status !== 'resolved' && (
              <button
                className="btn btn-outline-success btn-sm"
                style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem' }}
                onClick={() => onStatusChange(item.id, 'resolved')}
              >
                Resolve
              </button>
            )}
            {item.status !== 'new' && (
              <button
                className="btn btn-outline-danger btn-sm"
                style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem' }}
                onClick={() => onStatusChange(item.id, 'new')}
              >
                Reopen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
